import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const PrivacyPolicy = ({ onOpenForm }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy | Register Business Name Trademark</title>
        <meta 
          name="description" 
          content="Our privacy policy explains how we collect, use, and protect your personal information when you use our trademark registration services. Learn about our data practices and your privacy rights." 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://registerbusinessnametrademark.com/privacy-policy" />
      </Helmet>
      
      <Header onOpenForm={onOpenForm} />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">Privacy Policy</h1>
              <p className="text-gray-600 text-lg">Last Updated: December 2025</p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to Register Business Name Trademark ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trademark registration services.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                </p>
              </section>

              {/* Information We Collect */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">2.1 Personal Information You Disclose to Us</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Register for our services or create an account</li>
                  <li>Express interest in obtaining information about our trademark services</li>
                  <li>Submit a trademark application or inquiry</li>
                  <li>Contact us via email, phone, or contact forms</li>
                  <li>Subscribe to our newsletter or marketing communications</li>
                </ul>

                <p className="text-gray-700 leading-relaxed mb-4">
                  The personal information we collect may include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Name and contact data (full name, email address, phone number, mailing address)</li>
                  <li>Business information (company name, business type, industry)</li>
                  <li>Trademark information (proposed marks, descriptions, classifications)</li>
                  <li>Payment information (processed securely through third-party payment processors)</li>
                  <li>Communications data (messages, inquiries, feedback)</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">2.2 Automatically Collected Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you visit our website, we automatically collect certain information about your device and browsing actions:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Log and usage data (IP address, browser type, operating system, access times)</li>
                  <li>Device information (device type, unique device identifiers)</li>
                  <li>Location data (general geographic location based on IP address)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use personal information collected via our website and services for the following business purposes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>To provide trademark services:</strong> Process your trademark applications, conduct searches, file with USPTO, and manage your registrations</li>
                  <li><strong>To communicate with you:</strong> Send service-related communications, updates on your applications, and respond to inquiries</li>
                  <li><strong>To process payments:</strong> Facilitate transactions and send invoices and payment confirmations</li>
                  <li><strong>To improve our services:</strong> Analyze usage patterns to enhance website functionality and user experience</li>
                  <li><strong>To send marketing communications:</strong> Send promotional materials, newsletters, and service updates (with your consent)</li>
                  <li><strong>To comply with legal obligations:</strong> Maintain records as required by law and respond to legal requests</li>
                  <li><strong>To protect our business:</strong> Prevent fraud, enforce terms of service, and protect user safety</li>
                </ul>
              </section>

              {/* Disclosure of Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">4. How We Share Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may share your information in the following situations:
                </p>
                
                <h3 className="text-xl font-semibold text-primary mb-3">4.1 Service Providers</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We share your information with third-party service providers who perform services on our behalf, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Payment processors (to handle secure transactions)</li>
                  <li>Email service providers (to send communications)</li>
                  <li>Cloud storage providers (to securely store data)</li>
                  <li>Analytics providers (to analyze website usage)</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">4.2 Government Agencies</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We share necessary information with the United States Patent and Trademark Office (USPTO) to process your trademark applications.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">4.3 Legal Requirements</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas).
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">4.4 Business Transfers</h3>
                <p className="text-gray-700 leading-relaxed">
                  If we are involved in a merger, acquisition, or asset sale, your personal information may be transferred to the acquiring entity.
                </p>
              </section>

              {/* Data Security */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">5. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure server infrastructure with regular security updates</li>
                  <li>Access controls limiting employee access to personal data</li>
                  <li>Regular security assessments and audits</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
                </p>
              </section>

              {/* Data Retention */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">6. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Specifically:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Client information is retained for the duration of our service relationship and for 7 years thereafter for legal and tax purposes</li>
                  <li>Trademark application records are maintained indefinitely to support ongoing registrations and renewals</li>
                  <li>Marketing communications data is retained until you unsubscribe</li>
                  <li>Website analytics data is retained for 2 years</li>
                </ul>
              </section>

              {/* Your Privacy Rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">7. Your Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Portability:</strong> Request a copy of your personal information in a structured format</li>
                  <li><strong>Objection:</strong> Object to our processing of your personal information</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  <li><strong>Withdrawal of consent:</strong> Withdraw consent for marketing communications at any time</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  To exercise these rights, please contact us at info@registerbusinessnametrademark.com. We will respond to your request within 30 days.
                </p>
              </section>

              {/* Cookies and Tracking */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">8. Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to track activity on our website and store certain information. Types of cookies we use:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Essential Cookies:</strong> Necessary for website operation and security</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                  <li><strong>Marketing Cookies:</strong> Track visitors across websites to display relevant advertisements</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
              </section>

              {/* Third-Party Links */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">9. Third-Party Websites</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </section>

              {/* Children's Privacy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">10. Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately so we can delete it.
                </p>
              </section>

              {/* Updates to Policy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">11. Updates to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">12. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-accent/30 rounded-lg p-6">
                  <p className="text-gray-700 mb-2"><strong>Register Business Name Trademark</strong></p>
                  <p className="text-gray-700 mb-2">360 S Market St, San Jose, CA 95113</p>
                  <p className="text-gray-700 mb-2">Email: info@registerbusinessnametrademark.com</p>
                  <p className="text-gray-700">Phone: +1 (123) 123-4567</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer onOpenForm={onOpenForm} />
    </div>
  );
};

export default PrivacyPolicy;
